import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContractStatus, type RobotSummaryDto } from '@bhops/shared';
import type { CustomerSummaryDto } from '@bhops/shared';
import { robotsApi } from '@/api/robots';
import { contractsApi } from '@/api/contracts';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { CustomerPicker } from '@/components/pickers/CustomerPicker';
import { customerPickerEntityForRobot } from './robot-entity';
import { CONTRACT_STATUS_LABELS } from '@/lib/audit-log-format';

const MIN_REASON = 10;

interface LinkProps {
  open: boolean;
  onClose: () => void;
  robot: RobotSummaryDto;
}

/**
 * 사후 계약 연결(2026-09-01 오너 결정 Q2 — plan retire-rollback-sale-kind-2026-09 §2-2).
 *
 * 이미 나가 있거나 판매 완료된 로봇을 계약에 연결한다. "먼저 나가고 서류가
 * 뒤따라온" 로봇(#2110·#346 류)의 구분이 영영 빈칸으로 남는 구멍을 막는 경로 —
 * 재고 로봇 전용 '계약에 배정'(배정 인박스)과 별개다. 고객사 → 활성(ACTIVE)
 * 계약 순으로 사람이 지목하고, 사유가 로봇 기록·감사 장부에 남는다.
 */
export function RobotContractLinkModal({ open, onClose, robot }: LinkProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [contractId, setContractId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCustomerId(null);
      setCustomerName(null);
      setContractId('');
      setReason('');
      setError(null);
    }
  }, [open, robot]);

  const contractsQ = useQuery({
    queryKey: ['contracts', 'for-robot-link', customerId],
    queryFn: () => contractsApi.list({ customerId: customerId!, pageSize: 50 }),
    enabled: open && !!customerId,
  });
  const contracts = contractsQ.data?.items ?? [];

  const link = useMutation({
    mutationFn: () => robotsApi.linkContract(robot.id, { contractId, reason: reason.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robot.id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robot.id] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      toast.success('계약에 연결되었습니다. 사유가 기록에 남았습니다.', '계약 연결');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const reasonTooShort = reason.trim().length < MIN_REASON;

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (!contractId) {
      setError('연결할 계약을 선택해 주세요.');
      return;
    }
    if (reasonTooShort) {
      setError(`연결 사유를 ${MIN_REASON}자 이상 입력해 주세요.`);
      return;
    }
    link.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="계약에 사후 연결"
      width={520}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="contract-link-form"
            disabled={link.isPending || !contractId || reasonTooShort}
          >
            {link.isPending ? '연결 중…' : '연결'}
          </Button>
        </>
      }
    >
      <form id="contract-link-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div className="kuun-caption" style={{ marginBottom: 12, lineHeight: 1.5 }}>
          이미 나가 있는 로봇을 계약 서류와 연결합니다. 연결하면 목록과 CSV에 보이는 판매/구독
          구분이 그 계약을 기준으로 계산됩니다. 직접 정정해 둔 구분이 있다면 그대로 남습니다.
        </div>

        <Field label="고객사" htmlFor="link-customer" required>
          <CustomerPicker
            selected={customerId}
            onSelect={(c: CustomerSummaryDto) => {
              setCustomerId(c.id);
              setCustomerName(c.displayName);
              setContractId('');
            }}
            // 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 계약에 연결된다(서버
            // canUseRobotAt). 고르개는 이 로봇을 받을 수 있는 법인의 고객사만: 직원은 자기
            // 법인, 관리자는 비욘드허니컴 로봇이면 비욘드허니컴으로 좁히고(허니컴로보틱스
            // 계약에는 못 간다) 허니컴로보틱스 로봇이면 좁히지 않는다. 연결은 로봇의 소유
            // 법인을 바꾸지 않는다.
            ownerEntity={customerPickerEntityForRobot(user, robot.ownerEntity)}
          />
        </Field>

        <Field
          label="계약"
          htmlFor="link-contract"
          required
          hint="활성 계약만 연결할 수 있습니다. 초안 계약이라면 고객사 마스터에서 계약일을 먼저 입력해 주세요."
        >
          <Select
            id="link-contract"
            value={contractId}
            onChange={(e) => setContractId(e.target.value)}
            disabled={!customerId || contractsQ.isLoading}
          >
            <option value="">
              {!customerId
                ? '고객사를 먼저 선택해 주세요'
                : contractsQ.isLoading
                  ? '불러오는 중…'
                  : contracts.length === 0
                    ? `${customerName ?? '고객사'}의 계약이 없습니다`
                    : '계약 선택'}
            </option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id} disabled={c.status !== ContractStatus.ACTIVE}>
                {c.humanId} · {CONTRACT_STATUS_LABELS[c.status]}
                {c.status !== ContractStatus.ACTIVE ? ' (연결 불가)' : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="연결 사유"
          htmlFor="link-reason"
          required
          hint={`사후에 연결하는 이유를 ${MIN_REASON}자 이상 적어 주세요.`}
        >
          <Textarea
            id="link-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 판매 계약 서류가 출고 후에 등록되어 사후 연결"
          />
        </Field>
      </form>
    </Modal>
  );
}

interface UnlinkProps {
  open: boolean;
  onClose: () => void;
  robot: RobotSummaryDto;
}

/**
 * 사후 계약 연결 해제(plan §2-2 보완) — 잘못 건 연결의 되돌림. 재고 로봇의
 * '배정 해제'(제조 배정 인박스 흐름)와 달리 장부 연결만 끊는다.
 */
export function RobotContractUnlinkModal({ open, onClose, robot }: UnlinkProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open, robot]);

  const unlink = useMutation({
    mutationFn: () => robotsApi.unlinkContract(robot.id, { reason: reason.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robot.id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robot.id] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      toast.success('계약 연결이 해제되었습니다. 사유가 기록에 남았습니다.', '연결 해제');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const reasonTooShort = reason.trim().length < MIN_REASON;

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (reasonTooShort) {
      setError(`해제 사유를 ${MIN_REASON}자 이상 입력해 주세요.`);
      return;
    }
    unlink.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="계약 연결 해제"
      width={480}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="contract-unlink-form"
            variant="danger"
            disabled={unlink.isPending || reasonTooShort}
          >
            {unlink.isPending ? '해제 중…' : '연결 해제'}
          </Button>
        </>
      }
    >
      <form id="contract-unlink-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>
        <div className="kuun-caption" style={{ marginBottom: 12, lineHeight: 1.5 }}>
          {`이 로봇과 계약 ${robot.reservedContract?.humanId ?? ''}의 연결을 해제합니다. 판매/구독 구분은 계약 연결이 없을 때의 계산으로 돌아갑니다.`}
        </div>
        <Field
          label="해제 사유"
          htmlFor="unlink-reason"
          required
          hint={`해제하는 이유를 ${MIN_REASON}자 이상 적어 주세요.`}
        >
          <Textarea
            id="unlink-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 잘못된 계약에 연결되어 해제 후 올바른 계약으로 재연결"
          />
        </Field>
      </form>
    </Modal>
  );
}
